// Keycloak login with OpenID Connect Authorization Code + PKCE (no client secret,
// no implicit flow). Web: full-page redirect. Android: the same redirect, but inside the
// app WebView (capacitor.config allowNavigation) – the user never leaves the app.
//
// Google / Zalo are configured inside Keycloak as identity providers; we only pass
// `kc_idp_hint` to jump straight to them.

import { remoteConfig } from '../config';

interface Tokens {
  access: string;
  refresh: string | null;
  idToken: string | null;
  /** epoch ms */
  expiresAt: number;
  refreshExpiresAt: number | null;
}

interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

const KEY = 'booth.auth';
const PENDING = 'booth.auth.pending';

// Android too: the login page opens INSIDE the app's WebView (capacitor.config allowNavigation)
// and Keycloak redirects back to https://localhost/, i.e. the app itself – no browser.
const redirectUri = () => location.origin + location.pathname;

const ls = {
  get<T>(k: string): T | null { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
  set(k: string, v: unknown) { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch { /* */ } },
};

let tokens: Tokens | null = ls.get<Tokens>(KEY);
const listeners = new Set<(loggedIn: boolean) => void>();
export const onAuthChange = (fn: (loggedIn: boolean) => void) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((fn) => fn(!!tokens));

function save(t: Tokens | null) {
  tokens = t;
  ls.set(KEY, t);
  emit();
}

let discovery: Promise<Discovery> | null = null;
async function oidc(): Promise<{ d: Discovery; clientId: string; issuer: string }> {
  const cfg = await remoteConfig();
  if (!cfg) throw new Error('Không kết nối được máy chủ');
  discovery ??= fetch(`${cfg.issuer}/.well-known/openid-configuration`).then((r) => {
    if (!r.ok) throw new Error('Không đọc được cấu hình đăng nhập');
    return r.json();
  });
  return { d: await discovery, clientId: cfg.clientId, issuer: cfg.issuer };
}

const b64url = (buf: ArrayBuffer | Uint8Array) =>
  btoa(String.fromCharCode(...new Uint8Array(buf as ArrayBuffer))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

function randomString(bytes = 32) {
  return b64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function challenge(verifier: string) {
  return b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
}

export function isLoggedIn() {
  return !!tokens && (tokens.refreshExpiresAt == null || tokens.refreshExpiresAt > Date.now() || tokens.expiresAt > Date.now());
}

/** Starts login. action 'register' opens Keycloak's sign-up page; idp jumps to Google/Zalo. */
export async function login(opts: { idp?: string; register?: boolean; returnTo?: string; kcAction?: 'delete_account' } = {}) {
  const { d, clientId } = await oidc();
  const verifier = randomString(48);
  const state = randomString(16);
  const nonce = randomString(16);
  sessionOrLocal.set(PENDING, { verifier, state, nonce, returnTo: opts.returnTo ?? location.hash, at: Date.now() });
  const p = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid profile email',
    code_challenge: await challenge(verifier),
    code_challenge_method: 'S256',
    state,
    nonce,
    ui_locales: 'vi',
  });
  if (opts.idp) p.set('kc_idp_hint', opts.idp);
  if (opts.register) p.set('prompt', 'create');
  // application-initiated action, e.g. Keycloak's own "delete account" confirmation
  if (opts.kcAction) p.set('kc_action', opts.kcAction);
  const url = `${d.authorization_endpoint}?${p}`;
  location.assign(url);
}

/** The pending login lives in localStorage on Android (the app may be restarted by the browser). */
const sessionOrLocal = {
  get: () => ls.get<{ verifier: string; state: string; nonce: string; returnTo: string; at: number }>(PENDING),
  set: (k: string, v: unknown) => ls.set(k, v),
  clear: () => ls.set(PENDING, null),
};

/**
 * Finishes login if `url` is an auth callback. Returns the hash route to go to, or null.
 * Web: call with location.href on start-up. Android: call from the appUrlOpen deep link.
 */
export async function handleCallback(url: string): Promise<string | null> {
  const u = new URL(url);
  const params = u.searchParams;
  // return from a Keycloak application-initiated action (delete_account): no code to exchange
  const kcStatus = params.get('kc_action_status');
  if (kcStatus) {
    sessionOrLocal.clear();
    save(null);
    history.replaceState(null, '', location.pathname + '#/login');
    if (kcStatus === 'success') return '#/login';
    throw new Error(kcStatus === 'cancelled' ? 'Đã huỷ xoá tài khoản đăng nhập' : 'Chưa xoá được tài khoản đăng nhập');
  }
  if (!params.has('state') || !(params.has('code') || params.has('error'))) return null;
  const pending = sessionOrLocal.get();
  sessionOrLocal.clear();
  history.replaceState(null, '', location.pathname + (pending?.returnTo ?? ''));
  if (params.get('error')) throw new Error(params.get('error_description') ?? params.get('error')!);
  if (!pending || pending.state !== params.get('state') || Date.now() - pending.at > 10 * 60_000)
    throw new Error('Phiên đăng nhập không hợp lệ, thử lại nhé');
  const { d, clientId } = await oidc();
  const r = await fetch(d.token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code: params.get('code')!,
      redirect_uri: redirectUri(),
      client_id: clientId,
      code_verifier: pending.verifier,
    }),
  });
  if (!r.ok) throw new Error('Đăng nhập thất bại');
  const t = await r.json();
  if (t.id_token) {
    const claims = JSON.parse(atob(t.id_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (claims.nonce !== pending.nonce) throw new Error('Phiên đăng nhập không hợp lệ');
  }
  save(toTokens(t));
  return pending.returnTo || '#/feed';
}

function toTokens(t: any): Tokens {
  const now = Date.now();
  return {
    access: t.access_token,
    refresh: t.refresh_token ?? null,
    idToken: t.id_token ?? null,
    expiresAt: now + (t.expires_in ?? 300) * 1000,
    refreshExpiresAt: t.refresh_expires_in ? now + t.refresh_expires_in * 1000 : null,
  };
}

let refreshing: Promise<string | null> | null = null;

/** A valid access token (refreshed when about to expire), or null when logged out. */
export async function accessToken(): Promise<string | null> {
  if (!tokens) return null;
  if (tokens.expiresAt - 30_000 > Date.now()) return tokens.access;
  refreshing ??= (async () => {
    try {
      if (!tokens?.refresh) throw new Error('no refresh token');
      const { d, clientId } = await oidc();
      const r = await fetch(d.token_endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ grant_type: 'refresh_token', refresh_token: tokens.refresh, client_id: clientId }),
      });
      if (!r.ok) throw new Error('refresh failed');
      save(toTokens(await r.json()));
      return tokens!.access;
    } catch {
      save(null);
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Forces a refresh (e.g. a 401 from the realtime stream): returns the new token or null. */
export async function freshAccessToken(): Promise<string | null> {
  if (!tokens) return null;
  tokens = { ...tokens, expiresAt: 0 };
  return accessToken();
}

/** Runs before logout clears the tokens (e.g. unregister the push token while still authorised). */
const beforeLogout = new Set<() => Promise<unknown>>();
export const onBeforeLogout = (fn: () => Promise<unknown>) => { beforeLogout.add(fn); return () => beforeLogout.delete(fn); };

/** Ends the Keycloak session too (back-channel, works on web and Android). */
export async function logout() {
  await Promise.allSettled([...beforeLogout].map((fn) => fn()));
  const t = tokens;
  save(null);
  if (!t?.refresh) return;
  try {
    const { d, clientId } = await oidc();
    if (d.end_session_endpoint)
      await fetch(d.end_session_endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ client_id: clientId, refresh_token: t.refresh }),
      });
  } catch { /* already logged out locally */ }
}

/** Forget the session locally (e.g. API answered 401). */
export function dropSession() {
  save(null);
}
